/**
 * Algoritmos de geração de bolões (fechamentos).
 * - complete: todas as combinações C(n, k)
 * - closure: cobertura estratégica buscando X acertos se Y forem sorteados
 */

import { lookupCovering, applyCovering } from "./coverings";
import { lookupLotofacilStrategicCovering } from "./coverings/lotofacil-strategic";
import { STRATEGIC, getStrategicTarget } from "./strategic-matrix";

export type LotterySlug = "mega-sena" | "lotofacil" | "quina" | "dupla-sena";

export interface PoolLimits {
  minSelected: number;
  maxSelected: number;
  baseNumbers: number;
  /** Máximo de dezenas POR JOGO aceito pela Caixa (limite do volante). */
  maxPickPerGame: number;
}

/** Limites oficiais da Caixa por loteria. */
export const POOL_LIMITS: Record<LotterySlug, PoolLimits> = {
  "mega-sena":  { minSelected: 6,  maxSelected: 30, baseNumbers: 6,  maxPickPerGame: 20 },
  lotofacil:    { minSelected: 15, maxSelected: 25, baseNumbers: 15, maxPickPerGame: 20 },
  quina:        { minSelected: 5,  maxSelected: 40, baseNumbers: 5,  maxPickPerGame: 15 },
  "dupla-sena": { minSelected: 6,  maxSelected: 30, baseNumbers: 6,  maxPickPerGame: 15 },
};

/**
 * Tabela de preços base (R$ por jogo simples).
 * Os valores podem ser sobrescritos em tempo de execução pelo Painel Admin
 * (app_settings.lottery_prices) via `setLotteryBasePrices`.
 */
export const BASE_PRICE: Record<LotterySlug, number> = {
  "mega-sena": 6.0,
  lotofacil: 3.5,
  quina: 3.0,
  "dupla-sena": 3.0,
};

/** Atualiza os preços-base utilizados por `ticketPrice` (usado pelo loader admin). */
export function setLotteryBasePrices(prices: Partial<Record<LotterySlug, number>>) {
  (Object.keys(prices) as LotterySlug[]).forEach((slug) => {
    const v = prices[slug];
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) {
      BASE_PRICE[slug] = v;
    }
  });
}

/**
 * Valor default para o campo "Se acertar Y dezenas" (guaranteeIf) na UI
 * de Fechamento Estratégico, decidido por loteria:
 *  - Mega/Quina/Dupla → Y = meta de acertos (covering design, mais conservador)
 *  - Lotofácil       → Y = dezenas por jogo (lottery wheel, mais agressivo)
 */
export function defaultGuaranteeIf(slug: LotterySlug, k: number, guaranteeHits: number): number {
  if (slug === "lotofacil") return k;
  return guaranteeHits;
}

/** C(n, k) com BigInt para evitar overflow em valores altos. */
export function combinations(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let num = 1n;
  let den = 1n;
  for (let i = 0; i < k; i++) {
    num *= BigInt(n - i);
    den *= BigInt(i + 1);
  }
  return Number(num / den);
}

/** Preço de uma aposta com `picks` dezenas (segue a Caixa: C(picks, base)). */
export function ticketPrice(slug: LotterySlug, picks: number): number {
  const base = POOL_LIMITS[slug].baseNumbers;
  return combinations(picks, base) * BASE_PRICE[slug];
}

/** Gera todas as combinações C(numbers, k). */
export function generateComplete(numbers: number[], k: number): number[][] {
  const sorted = [...numbers].sort((a, b) => a - b);
  const result: number[][] = [];
  const buf: number[] = [];

  const recur = (start: number, depth: number) => {
    if (depth === k) {
      result.push([...buf]);
      return;
    }
    const remaining = k - depth;
    for (let i = start; i <= sorted.length - remaining; i++) {
      buf.push(sorted[i]);
      recur(i + 1, depth + 1);
      buf.pop();
    }
  };

  recur(0, 0);
  return result;
}

/**
 * Fechamento estratégico: dadas N dezenas, gera o menor conjunto possível
 * de combinações de tamanho `k` tal que, se Y dezenas saírem, ao menos
 * uma combinação tenha pelo menos `guaranteeHits` acertos.
 *
 * Implementação híbrida:
 *  1) Se o caso (v, k, t) — com v = n e t = guaranteeHits — tem cobertura
 *     ótima conhecida tabulada (LJCR), usa essa cobertura diretamente.
 *  2) Caso contrário, roda greedy set-cover seguido de "remoção de redundância"
 *     (descarta jogos cuja remoção não deixa nenhum alvo descoberto).
 *
 * Funciona bem para n <= 20 e guaranteeHits <= 5 (nossos limites).
 */
export function generateClosure(
  numbers: number[],
  k: number,
  guaranteeHits: number,
  guaranteeIf: number,
): number[][] {
  return generateClosureWithMeta(numbers, k, guaranteeHits, guaranteeIf).games;
}

export type ClosureSource = "ljcr" | "heuristic";

export interface ClosureResult {
  games: number[][];
  /** "ljcr" = cobertura ótima conhecida; "heuristic" = greedy + busca local. */
  source: ClosureSource;
  /** True quando o resultado é comprovadamente mínimo (tabela LJCR). */
  optimal: boolean;
}

/**
 * Versão de `generateClosure` que devolve metadados sobre a origem da solução.
 *
 * Semântica (lottery wheel L(v, k, p, t)):
 *   v = sorted.length         (dezenas escolhidas)
 *   k                         (dezenas por jogo)
 *   p = guaranteeIf           (quantas dezenas sorteadas estão dentro do pool)
 *   t = guaranteeHits         (acertos garantidos em algum jogo)
 *
 * Para cada possível sorteio S (p-subset de v), exigimos que algum jogo B
 * satisfaça |B ∩ S| >= t. Isso difere de um covering design C(v,k,t)
 * (que pede S ⊆ B para todo t-subset S) — covering é estritamente mais
 * forte e gera muito mais jogos. A interpretação correta para fechamentos
 * de loteria é a lottery wheel.
 */
export function generateClosureWithMeta(
  numbers: number[],
  k: number,
  guaranteeHits: number,
  guaranteeIf: number,
): ClosureResult {
  if (guaranteeHits > k) {
    throw new Error("Meta de acertos não pode ser maior que dezenas por jogo");
  }
  if (guaranteeIf < guaranteeHits) {
    throw new Error("Acertos garantidos exigem pelo menos esse número de dezenas sorteadas");
  }

  const sorted = [...numbers].sort((a, b) => a - b);
  const v = sorted.length;
  if (v > 31) {
    // Bitmask de 32 bits — limite de segurança da nossa representação.
    throw new Error("Pool de dezenas excede o limite suportado (31).");
  }

  // ─── Caminho rápido: tabela LJCR ────────────────────────────────────────
  // Uma cobertura C(v, k, t) é SEMPRE também uma wheel válida para qualquer
  // p >= t (se algum bloco contém um t-subset do sorteio, então
  // |bloco ∩ sorteio| >= t). Usamos como limite superior.
  const directBlocks = lookupCovering(v, k, guaranteeHits);
  const directLjcr =
    directBlocks && directBlocks.length > 0
      ? { games: applyCovering(directBlocks, sorted), size: directBlocks.length }
      : null;

  // Reformulação por complemento: a wheel L(v,k,k,t) é equivalente a
  // L(v, v-k, v-k, v-2k+t). Se k > v-k, a tabela LJCR de coberturas tende
  // a ter o caso menor disponível (ex.: Lotofácil k=15, v=20 → vira k'=5).
  // Uma C(v, k', t') (com k'=v-k, t'=v-2k+t) também é wheel válida na forma
  // dual: cada bloco B' (tamanho k') vira o complemento V\B' (tamanho k)
  // como jogo. Isso porque |B'∩S'| ≥ t' ⇔ |B∩S| ≥ t (B=V\B', S=V\S').
  let complementLjcr: { games: number[][]; size: number } | null = null;
  if (guaranteeIf === k && v - k > 0) {
    const kPrime = v - k;
    const tPrime = v - 2 * k + guaranteeHits;
    if (tPrime > 0 && tPrime <= kPrime) {
      const cBlocks = lookupCovering(v, kPrime, tPrime);
      if (cBlocks && cBlocks.length > 0) {
        // Cada bloco B' (índices 0..v-1, tamanho k') vira complemento
        const fullSet = new Set<number>(Array.from({ length: v }, (_, i) => i));
        const games = cBlocks.map((bp) => {
          const compIdx = [...fullSet].filter((i) => !bp.includes(i));
          return compIdx.map((i) => sorted[i]).sort((a, b) => a - b);
        });
        complementLjcr = { games, size: games.length };
      }
    }
  }

  // Escolhe a menor LJCR entre direta e complementar
  const ljcrCandidates = [directLjcr, complementLjcr].filter(
    (x): x is { games: number[][]; size: number } => x !== null,
  );
  const ljcr =
    ljcrCandidates.length > 0
      ? ljcrCandidates.reduce((a, b) => (a.size <= b.size ? a : b))
      : null;

  // ─── Greedy lazy (Minoux) sobre a wheel L(v, k, p, t) ───────────────────
  const nCand = Number(combinations(v, k));
  const nTarg = Number(combinations(v, guaranteeIf));
  // Cap defensivo: enumerar > 250k de qualquer lado fica inviável em JS sync.
  const FEASIBLE = nCand <= 250_000 && nTarg <= 250_000;

  if (!FEASIBLE) {
    if (ljcr) {
      return { games: ljcr.games, source: "ljcr", optimal: false };
    }
    throw new Error(
      `Caso muito grande para o algoritmo (candidatos=${nCand}, alvos=${nTarg}).`,
    );
  }

  const candidates = enumerateBitmasks(v, k);
  const targets = guaranteeIf === k ? candidates : enumerateBitmasks(v, guaranteeIf);
  const nC = candidates.length;
  const nT = targets.length;

  // Pré-computa coverList[c] = índices de targets que c cobre (popcount ≥ t).
  // Quando k === guaranteeIf candidates===targets e a relação é SIMÉTRICA,
  // então coverList serve também como inverted index (target→candidatos).
  const symmetric = candidates === targets;
  const { offsets: covOff, data: covData } = buildCoverList(
    candidates,
    targets,
    guaranteeHits,
  );
  const invOff = symmetric ? covOff : buildCoverList(targets, candidates, guaranteeHits).offsets;
  const invData = symmetric ? covData : buildCoverList(targets, candidates, guaranteeHits).data;

  // score[c] = quantos targets NÃO cobertos c ainda cobre.
  const score = new Int32Array(nC);
  for (let c = 0; c < nC; c++) score[c] = covOff[c + 1] - covOff[c];

  const covered = new Uint8Array(nT);
  const used = new Uint8Array(nC);
  const chosenIdx: number[] = [];
  let coveredCount = 0;

  while (coveredCount < nT) {
    // argmax(score) ignorando candidatos já usados.
    let bestIdx = -1;
    let bestScore = 0;
    for (let c = 0; c < nC; c++) {
      if (used[c]) continue;
      const s = score[c];
      if (s > bestScore) {
        bestScore = s;
        bestIdx = c;
      }
    }
    if (bestIdx < 0 || bestScore === 0) break;
    used[bestIdx] = 1;
    chosenIdx.push(bestIdx);
    // Marca targets cobertos e decrementa score dos demais candidatos.
    const start = covOff[bestIdx];
    const end = covOff[bestIdx + 1];
    for (let p = start; p < end; p++) {
      const t = covData[p];
      if (covered[t]) continue;
      covered[t] = 1;
      coveredCount++;
      // Decrementa score de todos os candidatos que cobrem t.
      const is = invOff[t];
      const ie = invOff[t + 1];
      for (let q = is; q < ie; q++) {
        score[invData[q]]--;
      }
    }
  }

  const heuristicGames = chosenIdx.map((i) => bitmaskToNumbers(candidates[i], sorted));

  // Pós-processo: remoção de redundância — tenta remover cada jogo;
  // mantém apenas os indispensáveis.
  const finalIdx = removeRedundant(chosenIdx, candidates, targets, guaranteeHits, nT);
  const finalGames = finalIdx.map((i) => bitmaskToNumbers(candidates[i], sorted));

  // Devolve o menor entre LJCR (válido como wheel) e a heurística.
  if (ljcr && ljcr.size <= finalGames.length) {
    return { games: ljcr.games, source: "ljcr", optimal: false };
  }
  return { games: finalGames, source: "heuristic", optimal: false };
}

/**
 * Remove jogos redundantes da solução greedy: para cada jogo, verifica se
 * todos os targets que ele cobria também estão cobertos por outros jogos.
 */
function removeRedundant(
  chosen: number[],
  candidates: Uint32Array,
  targets: Uint32Array,
  t: number,
  nT: number,
): number[] {
  const keep = new Uint8Array(chosen.length);
  keep.fill(1);
  // coverCount[targetIdx] = quantos jogos atualmente mantidos cobrem aquele target
  const coverCount = new Int32Array(nT);
  for (let i = 0; i < chosen.length; i++) {
    const m = candidates[chosen[i]];
    for (let j = 0; j < nT; j++) {
      if (popcount32(m & targets[j]) >= t) coverCount[j]++;
    }
  }
  // Tenta remover em ordem reversa (últimos escolhidos são menos importantes)
  for (let i = chosen.length - 1; i >= 0; i--) {
    const m = candidates[chosen[i]];
    // Pode remover se todos os targets cobertos por i têm coverCount > 1
    let removable = true;
    for (let j = 0; j < nT; j++) {
      if (popcount32(m & targets[j]) >= t && coverCount[j] <= 1) {
        removable = false;
        break;
      }
    }
    if (removable) {
      keep[i] = 0;
      for (let j = 0; j < nT; j++) {
        if (popcount32(m & targets[j]) >= t) coverCount[j]--;
      }
    }
  }
  return chosen.filter((_, i) => keep[i] === 1);
}

/**
 * Pré-visualização rápida: devolve o tamanho do fechamento SEM rodar a
 * heurística pesada — usa apenas as tabelas LJCR (direta e por complemento).
 * Útil para a UI atualizar a previsão em tempo real ao mexer nos sliders.
 * Retorna `null` quando não há tabela conhecida (o usuário precisa clicar
 * em "Gerar jogos" para ver o número real).
 */
export function previewClosureSize(
  v: number,
  k: number,
  guaranteeHits: number,
  guaranteeIf: number,
): number | null {
  if (v < k || guaranteeHits > k || guaranteeIf > v || guaranteeIf < guaranteeHits) {
    return null;
  }
  const direct = lookupCovering(v, k, guaranteeHits);
  let best: number | null = direct ? direct.length : null;
  if (guaranteeIf === k && v - k > 0) {
    const kPrime = v - k;
    const tPrime = v - 2 * k + guaranteeHits;
    if (tPrime > 0 && tPrime <= kPrime) {
      const comp = lookupCovering(v, kPrime, tPrime);
      if (comp) {
        if (best === null || comp.length < best) best = comp.length;
      }
    }
  }
  return best;
}

/**
 * Versão "completa" do preview: para pools pequenos roda o algoritmo
 * inteiro (rápido, < 200ms); para casos grandes devolve o tamanho da
 * melhor tabela LJCR conhecida (ou null se não houver).
 */
export function previewClosureSizeFull(
  numbers: number[],
  k: number,
  guaranteeHits: number,
  guaranteeIf: number,
): number | null {
  const v = numbers.length;
  if (v < k || guaranteeHits > k || guaranteeIf > v || guaranteeIf < guaranteeHits) {
    return null;
  }
  const nCand = Number(combinations(v, k));
  const nTarg = Number(combinations(v, guaranteeIf));
  // Limite para rodar a heurística sync no main thread (~200ms no v=12 k=6).
  const FAST = nCand * nTarg <= 80_000;
  if (FAST) {
    try {
      return generateClosureWithMeta(numbers, k, guaranteeHits, guaranteeIf).games.length;
    } catch {
      return null;
    }
  }
  return previewClosureSize(v, k, guaranteeHits, guaranteeIf);
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos para lottery wheel

/** Enumera todos os bitmasks de v bits com popcount=k. */
function enumerateBitmasks(v: number, k: number): Uint32Array {
  const total = Number(combinations(v, k));
  const out = new Uint32Array(total);
  let n = 0;
  // Gosper's hack
  let m = (1 << k) - 1;
  const limit = 1 << v;
  while (m < limit) {
    out[n++] = m >>> 0;
    const c = m & -m;
    const r = m + c;
    m = (((r ^ m) >>> 2) / c) | r;
  }
  return out;
}

/**
 * Constrói o "coverList": para cada candidato c em `cands`, lista os índices
 * de `targs` que c cobre (popcount da interseção ≥ t).
 * Layout flat: data[offsets[c]..offsets[c+1]] são os índices de targs cobertos.
 */
function buildCoverList(
  cands: Uint32Array,
  targs: Uint32Array,
  t: number,
): { offsets: Uint32Array; data: Uint16Array | Uint32Array } {
  const nC = cands.length;
  const nT = targs.length;
  const offsets = new Uint32Array(nC + 1);
  // Primeira passada: contar.
  let total = 0;
  for (let c = 0; c < nC; c++) {
    const mask = cands[c];
    let cnt = 0;
    for (let j = 0; j < nT; j++) {
      if (popcount32(mask & targs[j]) >= t) cnt++;
    }
    offsets[c + 1] = total + cnt;
    total += cnt;
  }
  // Aloca buffer adequado (Uint16 cabe se nT < 65536).
  const data =
    nT < 65536 ? new Uint16Array(total) : new Uint32Array(total);
  // Segunda passada: preencher.
  for (let c = 0; c < nC; c++) {
    const mask = cands[c];
    let p = offsets[c];
    for (let j = 0; j < nT; j++) {
      if (popcount32(mask & targs[j]) >= t) {
        data[p++] = j;
      }
    }
  }
  return { offsets, data };
}

/** popcount em 32 bits (Hamming weight). */
function popcount32(x: number): number {
  x = x - ((x >>> 1) & 0x55555555);
  x = (x & 0x33333333) + ((x >>> 2) & 0x33333333);
  x = (x + (x >>> 4)) & 0x0f0f0f0f;
  return (Math.imul(x, 0x01010101) >>> 24) & 0xff;
}

/** Converte bitmask em array de dezenas reais (sorted é o pool ordenado). */
function bitmaskToNumbers(mask: number, sorted: number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (mask & (1 << i)) out.push(sorted[i]);
  }
  return out;
}

/** Estatística rápida sobre um jogo. */
export function gameSummary(game: number[]): { sum: number; evens: number; odds: number } {
  const sum = game.reduce((a, b) => a + b, 0);
  const evens = game.filter((n) => n % 2 === 0).length;
  return { sum, evens, odds: game.length - evens };
}

/** Estima quantos jogos um fechamento vai gerar (limite superior = C(n,k)). */
export function estimateGames(
  mode: "complete" | "closure",
  selectedCount: number,
  k: number,
): number {
  return combinations(selectedCount, k);
}

/**
 * Estratégia "duplas": divide as dezenas escolhidas em duplas e cada jogo
 * final é a união de algumas duplas.
 *
 * - N par + k par: N/2 duplas, cada jogo une k/2 duplas.
 * - N ímpar + k ímpar: (N-1)/2 duplas e 1 dezena "âncora" (a que sobra),
 *   que entra em TODOS os jogos; cada jogo une (k-1)/2 duplas + âncora.
 *   Necessário para Lotofácil (15) e Quina (5), cujos jogos são ímpares.
 */
export function generatePairs(
  numbers: number[],
  k: number,
  customPairs?: [number, number][] | null,
): number[][] {
  const odd = numbers.length % 2 !== 0;
  if (odd && k % 2 === 0) {
    throw new Error(
      "Com quantidade ímpar de dezenas, as dezenas por jogo também devem ser ímpares (uma dezena âncora entra em todos os jogos)",
    );
  }
  if (!odd && k % 2 !== 0) {
    throw new Error("Dezenas por jogo deve ser par para combinação por duplas");
  }
  const expectedPairs = Math.floor(numbers.length / 2);
  let pairs: [number, number][];
  let anchor: number | null = null;
  if (customPairs && customPairs.length > 0) {
    // Valida: cobre todas as dezenas escolhidas exatamente uma vez
    const expected = new Set(numbers);
    const seen = new Set<number>();
    for (const [a, b] of customPairs) {
      if (a === b) throw new Error("Uma dupla não pode repetir a mesma dezena");
      if (!expected.has(a) || !expected.has(b)) {
        throw new Error("Duplas devem usar apenas dezenas selecionadas");
      }
      if (seen.has(a) || seen.has(b)) {
        throw new Error("Cada dezena só pode aparecer em uma dupla");
      }
      seen.add(a);
      seen.add(b);
    }
    if (seen.size !== expectedPairs * 2) {
      throw new Error(
        odd
          ? "Forme duplas com todas as dezenas, deixando apenas a dezena âncora de fora"
          : "Forme duplas com todas as dezenas selecionadas",
      );
    }
    pairs = customPairs.map(([a, b]) => [a, b] as [number, number]);
    if (odd) {
      anchor = numbers.find((n) => !seen.has(n)) ?? null;
    }
  } else {
    const sorted = [...numbers].sort((a, b) => a - b);
    pairs = [];
    for (let i = 0; i + 1 < sorted.length; i += 2) {
      pairs.push([sorted[i], sorted[i + 1]]);
    }
    if (odd) anchor = sorted[sorted.length - 1];
  }
  if (odd && anchor == null) {
    throw new Error("Não foi possível identificar a dezena âncora");
  }
  const pairsPerGame = odd ? (k - 1) / 2 : k / 2;
  if (pairsPerGame < 1) {
    throw new Error("Dezenas por jogo insuficientes para formar duplas");
  }
  if (pairsPerGame > pairs.length) {
    throw new Error("Dezenas por jogo excede o total de duplas formadas");
  }

  // Gera todas as combinações de `pairsPerGame` duplas dentre as N/2 duplas
  const idxCombos = generateComplete(
    Array.from({ length: pairs.length }, (_, i) => i),
    pairsPerGame,
  );
  const result: number[][] = idxCombos.map((idxs) => {
    const game: number[] = anchor != null ? [anchor] : [];
    for (const i of idxs) game.push(pairs[i][0], pairs[i][1]);
    return game.sort((a, b) => a - b);
  });
  return result;
}

/**
 * Retorna a dezena âncora de uma combinação por duplas, ou null quando não há.
 * A âncora é a dezena que sobra em seleções ímpares e entra em todos os jogos.
 * Com duplas customizadas, procura a dezena não usada em nenhuma dupla.
 * Sem duplas customizadas, usa a última dezena em ordem crescente (mesma
 * lógica do auto-emparelhamento de `generatePairs`).
 */
export function getPairsAnchor(
  numbers: number[],
  customPairs?: [number, number][] | null,
): number | null {
  if (numbers.length % 2 === 0) return null;
  if (customPairs && customPairs.length > 0) {
    const seen = new Set<number>();
    for (const [a, b] of customPairs) {
      seen.add(a);
      seen.add(b);
    }
    return numbers.find((n) => !seen.has(n)) ?? null;
  }
  const sorted = [...numbers].sort((a, b) => a - b);
  return sorted[sorted.length - 1] ?? null;
}

/** Quantos jogos a estratégia de duplas vai produzir. */
export function estimatePairs(selectedCount: number, k: number): number {
  if (selectedCount % 2 !== k % 2) return 0;
  if (selectedCount % 2 !== 0) {
    // Ímpar: (N-1)/2 duplas + 1 âncora fixa em todos os jogos.
    if (k < 3) return 0;
    return combinations((selectedCount - 1) / 2, (k - 1) / 2);
  }
  return combinations(selectedCount / 2, k / 2);
}

/**
 * Estratégia "fixas + variáveis": dezenas fixas aparecem em todos os jogos.
 * As demais (variáveis) são combinadas C(variáveis, k - fixas.length) e
 * cada combinação é unida com as fixas.
 */
export function generateFixed(
  fixedNumbers: number[],
  variableNumbers: number[],
  k: number,
): number[][] {
  const fixedSorted = [...new Set(fixedNumbers)].sort((a, b) => a - b);
  const variables = variableNumbers.filter((n) => !fixedSorted.includes(n));
  const remaining = k - fixedSorted.length;
  if (remaining < 0) {
    throw new Error("Mais dezenas fixas do que cabe no jogo");
  }
  if (remaining === 0) {
    return [fixedSorted];
  }
  if (remaining > variables.length) {
    throw new Error("Variáveis insuficientes para preencher o jogo");
  }
  const variableCombos = generateComplete(variables, remaining);
  return variableCombos.map((vc) => [...fixedSorted, ...vc].sort((a, b) => a - b));
}

/** Estima quantos jogos a estratégia fixas+variáveis vai produzir. */
export function estimateFixed(
  fixedCount: number,
  variableCount: number,
  k: number,
): number {
  const remaining = k - fixedCount;
  if (remaining < 0) return 0;
  if (remaining === 0) return 1;
  return combinations(variableCount, remaining);
}

// ════════════════════════════════════════════════════════════════════════════
// Fechamento Estratégico — API curada (matriz fixa por loteria)
// ════════════════════════════════════════════════════════════════════════════

export interface StrategicResult {
  games: number[][];
  /** Quantos jogos a referência publicada indica como mínimo. */
  target: number;
  /** Quantos jogos o gerador efetivamente produziu. */
  actual: number;
  /**
   * "melhor"     → actual < target (heurística bateu a referência publicada)
   * "exato"      → actual === target (resultado bate a referência)
   * "aproximacao"→ actual > target (heurística não chegou ao ótimo)
   */
  badge: "melhor" | "exato" | "aproximacao";
  /**
   * Quando `badge === "melhor"` (o app gera MENOS jogos que a referência),
   * rodamos uma verificação exaustiva da propriedade de cobertura:
   *   para CADA t-subconjunto das v dezenas escolhidas, existe pelo menos
   *   um jogo gerado que contém esse subconjunto.
   *
   *  - `true`  → cobertura matematicamente comprovada (selo "Cobertura verificada").
   *  - `false` → falhou (não deveria ocorrer; indica bug — exibimos alerta).
   *  - `undefined` → não aplicável (badge "exato" ou "aproximacao").
   */
  coverageVerified?: boolean;
  /** Quantos t-subconjuntos foram testados (apenas quando verificado). */
  coverageTested?: number;
}

/**
 * Gera o fechamento estratégico para uma loteria, respeitando a matriz
 * curada (k fixo, v ∈ lista, t ∈ lista). Sempre devolve `target` lado
 * a lado com `actual` para a UI mostrar a referência publicada.
 */
export function generateStrategic(
  slug: LotterySlug,
  numbers: number[],
  t: number,
): StrategicResult {
  const cfg = STRATEGIC[slug];
  if (!cfg) throw new Error(`Loteria sem matriz estratégica: ${slug}`);
  const v = numbers.length;
  if (!cfg.vOptions.includes(v)) {
    throw new Error(
      `Para ${slug} no Fechamento Estratégico use ${cfg.vOptions.join(", ")} dezenas.`,
    );
  }
  if (!cfg.tOptions.includes(t)) {
    throw new Error(`Meta deve ser ${cfg.tOptions.join(" ou ")} acertos.`);
  }
  const target = cfg.targets[v][t];
  const k = cfg.k;

  if (slug === "lotofacil") {
    const strategicBlocks = lookupLotofacilStrategicCovering(v, t);
    if (strategicBlocks && strategicBlocks.length > 0) {
      const games = applyCovering(strategicBlocks, numbers);
      return { games, target, actual: games.length, badge: games.length === target ? "exato" : games.length < target ? "melhor" : "aproximacao" };
    }
  }

  // Para Mega-Sena / Dupla-Sena / Quina, preferimos SEMPRE a cobertura
  // LJCR embarcada (publicada na literatura). Isso garante que a quantidade
  // gerada bata exatamente com a "previsão" exibida na UI — sem qualquer
  // divergência entre heurística local e referência publicada.
  {
    const sorted = [...numbers].sort((a, b) => a - b);
    const direct = lookupCovering(v, k, t);
    if (direct && direct.length > 0) {
      const games = applyCovering(direct, sorted);
      const actual = games.length;
      const badge: StrategicResult["badge"] =
        actual < target ? "melhor" : actual === target ? "exato" : "aproximacao";
      return { games, target, actual, badge };
    }
  }

  // Semântica clássica de "fechamento" brasileiro = covering design
  // C(v, k, t): se QUAISQUER t das v dezenas escolhidas saírem no sorteio,
  // algum jogo contém essas t. É exatamente o que a tabela de referência
  // (Bluskov / LJCR / literatura brasileira) mede — por isso usamos
  // guaranteeIf = t (e não k, que daria a wheel L(v,k,k,t), uma condição
  // muito mais fraca e que produzia números irrealisticamente baixos).
  const nCand = Number(combinations(v, k));
  const nTarg = Number(combinations(v, t));
  // Cap para manter o cálculo abaixo de ~3-5 s no navegador.
  // buildCoverList é O(nCand * nTarg) em duas passadas.
  const feasibleHeuristic = nCand <= 250_000 && nTarg <= 250_000 && nCand * nTarg <= 150_000_000;

  let best: number[][] | null = null;

  if (feasibleHeuristic) {
    try {
      const r = generateClosureWithMeta(numbers, k, t, t);
      best = r.games;
    } catch {
      best = null;
    }
  }

  // Fallback: usa LJCR direto se houver tabela embarcada.
  if (!best) {
    const sorted = [...numbers].sort((a, b) => a - b);
    const direct = lookupCovering(v, k, t);
    if (direct && direct.length > 0) {
      best = applyCovering(direct, sorted);
    }
  }

  // Se nada funcionou, é um caso que o motor atual não consegue gerar
  // no navegador (ex.: Lotofácil 20/22/24-15-13). Sinaliza honestamente
  // ao invés de devolver um número errado.
  if (!best) {
    const smaller = cfg.vOptions.filter((x) => x < v).slice(-1)[0];
    const fallbackHint = smaller
      ? `Reduza para ${smaller} dezenas ou escolha outra estratégia (Combinações completas, Por duplas, Fixas + variáveis).`
      : `Escolha outra estratégia (Combinações completas, Por duplas, Fixas + variáveis).`;
    throw new Error(
      `Fechamento ${v}-${k}-${t} (referência publicada: ${target} jogos) ainda ` +
      `não tem solver embarcado neste tamanho — o cálculo exato exigiria horas ` +
      `de processamento. ${fallbackHint}`,
    );
  }

  const actual = best.length;
  const badge: StrategicResult["badge"] =
    actual < target ? "melhor" : actual === target ? "exato" : "aproximacao";

  // Verificação de cobertura: só rodamos quando geramos MENOS jogos que a
  // referência publicada. Custo: C(v, t) iterações × verificação por jogo.
  // Para os tamanhos da matriz curada isso é < 50ms em JS sync.
  let coverageVerified: boolean | undefined;
  let coverageTested: number | undefined;
  if (badge === "melhor") {
    const check = verifyCoverage(best, numbers, t);
    coverageVerified = check.ok;
    coverageTested = check.tested;
  }
  return { games: best, target, actual, badge, coverageVerified, coverageTested };
}

/**
 * Verifica a propriedade de cobertura de um fechamento estratégico:
 * para cada t-subconjunto das `numbers` escolhidas pelo usuário, checa que
 * pelo menos um jogo de `games` contém todos esses t números.
 *
 * Equivale à definição formal de um covering design C(v, k, t).
 * Retorna `ok=true` quando todos os C(v, t) subconjuntos estão cobertos.
 */
export function verifyCoverage(
  games: number[][],
  numbers: number[],
  t: number,
): { ok: boolean; tested: number; missing: number[] | null } {
  const sorted = [...numbers].sort((a, b) => a - b);
  const v = sorted.length;
  if (v > 31) return { ok: false, tested: 0, missing: null };
  // Converte cada jogo num bitmask sobre os índices de `sorted`.
  const idx = new Map<number, number>();
  sorted.forEach((n, i) => idx.set(n, i));
  const gameMasks: number[] = [];
  for (const g of games) {
    let m = 0;
    for (const n of g) {
      const i = idx.get(n);
      if (i !== undefined) m |= 1 << i;
    }
    gameMasks.push(m >>> 0);
  }
  // Enumera todos os t-subconjuntos de v.
  const subsets = enumerateBitmasksPublic(v, t);
  let tested = 0;
  for (let s = 0; s < subsets.length; s++) {
    const sub = subsets[s];
    tested++;
    let covered = false;
    for (let g = 0; g < gameMasks.length; g++) {
      if ((gameMasks[g] & sub) === sub) {
        covered = true;
        break;
      }
    }
    if (!covered) {
      const missing: number[] = [];
      for (let i = 0; i < v; i++) if (sub & (1 << i)) missing.push(sorted[i]);
      return { ok: false, tested, missing };
    }
  }
  return { ok: true, tested, missing: null };
}

/** Wrapper público de enumerateBitmasks (mesma implementação). */
function enumerateBitmasksPublic(v: number, k: number): Uint32Array {
  return enumerateBitmasks(v, k);
}

/**
 * Prévia rápida: sempre devolve o target da matriz (sem rodar gerador).
 * Para mostrar antes de o usuário clicar em "Gerar combinações".
 */
export function previewStrategic(
  slug: LotterySlug,
  v: number,
  t: number,
): number | null {
  return getStrategicTarget(slug, v, t);
}
